/** Page liste des employés - pagination, recherche, création */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Table, type Column } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/shared/Pagination';
import { SearchInput } from '../components/shared/SearchInput';
import {
  useEmployees,
  useCreateEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from '../hooks/useEmployees';
import { useSites } from '../hooks/useSites';
import { ACTIVITY_LABEL, type Employee } from '../types';

const ACTIVITIES = [
  { value: 'office', label: ACTIVITY_LABEL.office },
  { value: 'boulangerie', label: ACTIVITY_LABEL.boulangerie },
  { value: 'traiteur', label: ACTIVITY_LABEL.traiteur },
  { value: 'chambre_froide', label: ACTIVITY_LABEL.chambre_froide },
  { value: 'cleaning', label: ACTIVITY_LABEL.cleaning },
  { value: 'driving', label: ACTIVITY_LABEL.driving },
] as const;

// Schéma de validation du formulaire de création
const createSchema = z.object({
  first_name: z.string().min(1, 'Prénom requis'),
  last_name: z.string().min(1, 'Nom requis'),
  site_id: z.coerce.number().min(1, 'Site requis'),
  email: z.string().email('Email invalide').or(z.literal('')).optional(),
  department: z.string().optional(),
  activity: z.enum(['office', 'boulangerie', 'traiteur', 'chambre_froide', 'cleaning', 'driving']).default('office'),
  badge_uid: z.string().min(8, '8 caractères minimum').or(z.literal('')).optional(),
});

type CreateFormData = z.infer<typeof createSchema>;

export function EmployeesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = useState(20);
  const [activeOnly, setActiveOnly] = useState(true);
  const [activityFilter, setActivityFilter] = useState<'' | 'boulangerie' | 'traiteur' | 'chambre_froide' | 'cleaning' | 'driving' | 'office'>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data, isLoading } = useEmployees({
    page,
    per_page: perPage,
    search,
    active_only: activeOnly,
    activity: activityFilter || undefined,
  });

  const createMutation = useCreateEmployee();
  const updateMutation = useUpdateEmployee();
  const deleteMutation = useDeleteEmployee();

  const { data: sites } = useSites();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFormData>({
    resolver: zodResolver(createSchema),
    defaultValues: { site_id: sites?.[0]?.id ?? 1, activity: 'office' },
  });

  const onSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    reset();
  };

  const openCreate = () => {
    setEditingId(null);
    reset({
      first_name: '',
      last_name: '',
      site_id: sites?.[0]?.id ?? 1,
      email: '',
      department: '',
      activity: 'office',
      badge_uid: '',
    });
    setModalOpen(true);
  };

  const openEdit = (emp: Employee) => {
    setEditingId(emp.id);
    reset({
      first_name: emp.first_name,
      last_name: emp.last_name,
      site_id: emp.site_id,
      email: emp.email ?? '',
      department: emp.department ?? '',
      activity: emp.activity,
      badge_uid: '',
    });
    setModalOpen(true);
  };

  const onSubmit = async (formData: CreateFormData) => {
    if (editingId) {
      // Le badge se gère via l'enrôlement, pas ici.
      await updateMutation.mutateAsync({
        id: editingId,
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          site_id: formData.site_id,
          email: formData.email || undefined,
          department: formData.department || undefined,
          activity: formData.activity,
        },
      });
    } else {
      await createMutation.mutateAsync({
        first_name: formData.first_name,
        last_name: formData.last_name,
        site_id: formData.site_id,
        email: formData.email || undefined,
        department: formData.department || undefined,
        activity: formData.activity,
        badge_uid: formData.badge_uid || undefined,
      });
    }
    closeModal();
  };

  const handleDelete = (id: number, name: string) => {
    if (window.confirm(`Désactiver ${name} ?`)) {
      deleteMutation.mutate(id);
    }
  };

  const columns: Column<Employee>[] = [
    {
      key: 'name',
      header: 'Nom',
      render: (emp) => (
        <button
          onClick={() => navigate(`/employees/${emp.id}`)}
          className="text-accent hover:underline font-medium"
        >
          {emp.first_name} {emp.last_name}
        </button>
      ),
    },
    {
      key: 'badge_uid',
      header: 'Badge',
      render: (emp) =>
        emp.badge_uid ? (
          <span className="font-mono text-xs text-gray-600">{emp.badge_uid}</span>
        ) : (
          <Badge variant="warning">Aucun</Badge>
        ),
    },
    { key: 'department', header: 'Département' },
    {
      key: 'activity',
      header: 'Activité',
      render: (emp) => (
        <span className="text-xs text-gray-600">{ACTIVITY_LABEL[emp.activity] ?? emp.activity}</span>
      ),
    },
    {
      key: 'site_name',
      header: 'Site',
      render: (emp) => emp.site_name || '—',
    },
    {
      key: 'is_active',
      header: 'Statut',
      render: (emp) => (
        <Badge variant={emp.is_active ? 'success' : 'danger'}>
          {emp.is_active ? 'Actif' : 'Inactif'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (emp) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEdit(emp)}
          >
            <Pencil size={13} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(emp.id, `${emp.first_name} ${emp.last_name}`)}
          >
            <Trash2 size={13} className="text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header
        title="Employés"
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} className="mr-1" /> Nouvel employé
          </Button>
        }
      />

      {/* Barre de recherche et filtres */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-full sm:w-72">
          <SearchInput value={search} onChange={onSearch} placeholder="Rechercher par nom..." />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(1);
            }}
            className="rounded border-gray-300"
          />
          Actifs uniquement
        </label>
        <select
          value={activityFilter}
          onChange={(e) => {
            setActivityFilter(e.target.value as typeof activityFilter);
            setPage(1);
          }}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600
                     focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <option value="">Toutes activités</option>
          {ACTIVITIES.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <select
          value={perPage}
          onChange={(e) => {
            setPerPage(Number(e.target.value));
            setPage(1);
          }}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600
                     focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </div>

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          data={data?.items ?? []}
          loading={isLoading}
          emptyMessage="Aucun employé trouvé"
        />
        {data && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Modal création */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId ? "Modifier l'employé" : 'Nouvel employé'}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <form className="space-y-3">
          <Input label="Prénom" {...register('first_name')} error={errors.first_name?.message} />
          <Input label="Nom" {...register('last_name')} error={errors.last_name?.message} />
          <Input label="Email" type="email" {...register('email')} error={errors.email?.message} />
          <Input label="Département" {...register('department')} />
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">Activité</label>
            <select
              {...register('activity')}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                         focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            >
              {ACTIVITIES.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">Site</label>
            <select
              {...register('site_id')}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                         focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            >
              {sites?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          {!editingId && (
            <Input
              label="Badge UID (optionnel)"
              {...register('badge_uid')}
              error={errors.badge_uid?.message}
              placeholder="04A1B2C3D4E5F6"
            />
          )}
        </form>
      </Modal>
    </div>
  );
}
